import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { formatCentsExact, MESES } from '@/features/catalog/catalog';
import { listAllClients, type Client } from '@/features/clients/clients';
import {
  acceptProposedKm,
  listBatches,
  listClientMappings,
  listReconciliation,
  reconcileBatch,
  resolveClient,
  type ClientMapping,
  type ImportBatch,
  type ReconciliationRow,
} from './migration';

interface Props {
  organizationId: string;
  isAdmin: boolean;
}

const ETIQUETA_CLASE: Record<string, string> = {
  exacta: 'Coincide',
  bug_legacy: 'Defecto conocido del sistema anterior',
  normalizacion: 'Redondeo explicado',
  pendiente: 'Dato pendiente',
  error_migracion: 'ERROR DE MIGRACION',
};

const ETIQUETA_CONFIANZA: Record<ClientMapping['confidence'], string> = {
  // `exact` es "el texto coincide sin ambigüedad", no "ya está aplicado":
  // llamarlo "Resuelto" mientras espera en la lista de pendientes es mentir.
  exact: 'Coincidencia exacta — falta aplicar',
  probable: 'Probable — falta confirmar',
  ambiguous: 'Ambiguo — decidí vos',
};

/**
 * Migración del histórico (Spec §51, §52).
 *
 * Dos cosas que esta pantalla NO hace, a propósito:
 *
 * · No fusiona clientes. Muestra los textos legacy que no pudo resolver y
 *   espera una decisión, con el motivo escrito. Unir dos clientes por parecido
 *   de nombre es irreversible y se lleva puesto el histórico de los dos.
 * · No corrige diferencias. Las lista. Una diferencia entre lo que decía el
 *   sistema viejo y lo que calcula este es información, no un error a tapar.
 */
export function MigrationCard({ organizationId, isAdmin }: Props) {
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [mappings, setMappings] = useState<ClientMapping[]>([]);
  const [rows, setRows] = useState<ReconciliationRow[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [resolviendo, setResolviendo] = useState<string | null>(null);
  const [clienteElegido, setClienteElegido] = useState('');
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    let cancelado = false;
    void (async () => {
      try {
        const [bs, cs] = await Promise.all([
          listBatches(organizationId),
          listAllClients(organizationId),
        ]);
        if (cancelado) return;
        setBatches(bs);
        setClients(cs);
        setBatchId((actual) => actual ?? bs[0]?.id ?? null);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [organizationId, isAdmin, recarga]);

  useEffect(() => {
    if (!batchId) return;
    let cancelado = false;
    void (async () => {
      try {
        const [ms, rs] = await Promise.all([
          listClientMappings(batchId),
          listReconciliation(batchId),
        ]);
        if (cancelado) return;
        setMappings(ms);
        setRows(rs);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [batchId, recarga]);

  // Un administrador es quien puede tocar esto; para el resto ni se muestra,
  // aunque la seguridad real la aplican las policies, no este `if`.
  if (!isAdmin) return null;
  if (batches.length === 0) return null;

  const recargar = () => setRecarga((n) => n + 1);

  const conciliar = async () => {
    if (!batchId) return;
    setError(null);
    setAviso(null);
    try {
      await reconcileBatch(batchId);
      setAviso('Conciliación recalculada.');
      recargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const resolver = async (m: ClientMapping) => {
    setError(null);
    setAviso(null);
    try {
      const r = await resolveClient({
        mappingId: m.id,
        clientId: clienteElegido,
        reason: motivo,
      });
      setAviso(
        `Resuelto. ${r.trabajos_movidos} trabajo(s) reasignados` +
          (r.trabajos_bloqueados > 0
            ? `; ${r.trabajos_bloqueados} quedaron sin mover por estar en un período cerrado.`
            : '.'),
      );
      setResolviendo(null);
      setClienteElegido('');
      setMotivo('');
      recargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const aceptarKm = async (m: ClientMapping) => {
    setError(null);
    setAviso(null);
    try {
      await acceptProposedKm(m.id);
      setAviso('Kilometraje aceptado y registrado en la bitácora.');
      recargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const pendientes = mappings.filter((m) => m.resolved_at === null);
  const porTrabajo = rows.filter((r) => r.scope === 'trabajo');
  const porParticipante = rows.filter((r) => r.scope === 'participante');
  // La unica categoria que bloquea. Ninguna otra frena la migracion.
  const bloqueantes = rows.filter((r) => r.kind === 'error_migracion');

  return (
    <section className="flex flex-col gap-4 superficie p-4" data-testid="migration-card">
      <div>
        <h2 className="font-medium">Migración del histórico</h2>
        <p className="text-sm text-[var(--tinta-suave)]">
          Lo importado del sistema anterior. Nada se fusiona ni se corrige solo.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="migration-error">
          {error}
        </p>
      )}
      {aviso && (
        <p className="text-sm text-[var(--exito)]" data-testid="migration-notice">
          {aviso}
        </p>
      )}

      <label className="flex flex-col gap-1 text-sm">
        Lote importado
        <select
          value={batchId ?? ''}
          onChange={(e) => setBatchId(e.target.value)}
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2 text-base"
          data-testid="batch-select"
        >
          {batches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.source} · corte {b.cutoff_date}
            </option>
          ))}
        </select>
      </label>

      {/* --- Clientes que esperan una decisión (tarea 10.5) --- */}
      <div className="flex flex-col gap-2">
        <h3 className="text-xs font-medium uppercase opacity-60">
          Clientes por resolver ({pendientes.length})
        </h3>

        {pendientes.length === 0 ? (
          <p className="text-sm text-[var(--tinta-suave)]">
            No queda ningún texto legacy sin resolver.
          </p>
        ) : (
          <ul
            className="flex flex-col divide-y divide-[var(--linea)]"
            data-testid="pending-mappings"
          >
            {pendientes.map((m) => (
              <li key={m.id} className="flex flex-col gap-2 py-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block break-words font-medium">{m.legacy_text}</span>
                    <span className="block text-xs text-[var(--tinta-suave)]">
                      {ETIQUETA_CONFIANZA[m.confidence]} · {m.occurrences} trabajo
                      {m.occurrences === 1 ? '' : 's'}
                    </span>
                    {m.trailing_number !== null && (
                      <span className="block text-xs text-[var(--aviso)]">
                        El nombre termina en {m.trailing_number}.
                        {m.proposed_km !== null
                          ? ' Hay evidencia externa registrada.'
                          : ' Un número en el nombre no son kilómetros: hace falta evidencia externa.'}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setResolviendo(resolviendo === m.id ? null : m.id);
                      setClienteElegido('');
                      setMotivo('');
                    }}
                    className="min-h-11 shrink-0 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm"
                    data-testid="resolve-toggle"
                  >
                    Resolver
                  </button>
                </div>

                {resolviendo === m.id && (
                  <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3">
                    <label className="flex flex-col gap-1 text-sm">
                      ¿A qué cliente corresponde?
                      <select
                        value={clienteElegido}
                        onChange={(e) => setClienteElegido(e.target.value)}
                        className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2 text-base"
                        data-testid="resolve-client"
                      >
                        <option value="">Elegir…</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      ¿En qué te basás?
                      <input
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        placeholder="Quién lo confirmó, o con qué dato"
                        className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
                        data-testid="resolve-reason"
                      />
                      <span className="text-xs text-[var(--tinta-tenue)]">
                        Obligatorio: queda en la bitácora junto al texto original.
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => void resolver(m)}
                      disabled={clienteElegido === '' || motivo.trim() === ''}
                      className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)] disabled:opacity-50"
                      data-testid="resolve-confirm"
                    >
                      Asignar a este cliente
                    </button>
                    <p className="text-xs text-[var(--tinta-tenue)]">
                      Sin indicar establecimiento, los trabajos van al campo &ldquo;Sin especificar
                      (legacy)&rdquo;. No se inventan nombres de campos.
                    </p>
                  </div>
                )}

                {m.proposed_km !== null && (
                  <button
                    type="button"
                    onClick={() => void aceptarKm(m)}
                    className="min-h-11 self-start rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm"
                    data-testid="accept-km"
                  >
                    Aceptar {m.proposed_km} km como valor por defecto
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* --- Conciliación (tarea 10.9) --- */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xs font-medium uppercase opacity-60">Conciliación</h3>
          <button
            type="button"
            onClick={() => void conciliar()}
            className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm"
            data-testid="reconcile"
          >
            Recalcular
          </button>
        </div>

        <p className="text-sm" data-testid="reconcile-summary">
          {porTrabajo.length} trabajo{porTrabajo.length === 1 ? '' : 's'} ·{' '}
          {porTrabajo.filter((r) => r.kind === 'exacta').length} coinciden al centavo.
          {porParticipante.length > 0 && (
            <>
              {' '}
              Por participante: {
                porParticipante.filter((r) => r.kind === 'exacta').length
              } exactas, {porParticipante.filter((r) => r.kind === 'bug_legacy').length} por defecto
              del sistema anterior,{' '}
              {porParticipante.filter((r) => r.kind === 'normalizacion').length} por redondeo.
            </>
          )}
        </p>

        {bloqueantes.length > 0 ? (
          <p
            className="rounded-[var(--radius-control)] bg-[var(--peligro-fondo)] px-3 py-2 text-sm text-[var(--peligro)]"
            data-testid="unexplained"
          >
            Hay {bloqueantes.length} diferencia(s) clasificadas como ERROR DE MIGRACIÓN. Eso
            bloquea: la migración no puede darse por buena hasta entenderlas.
          </p>
        ) : (
          <p className="text-xs text-[var(--tinta-tenue)]">
            Ninguna diferencia quedó clasificada como error de migración.
          </p>
        )}

        {porParticipante.filter((r) => r.diff_cents !== 0).length > 0 && (
          <ul
            className="flex flex-col divide-y divide-[var(--linea)] text-sm"
            data-testid="reconcile-diffs"
          >
            {porParticipante
              .filter((r) => r.diff_cents !== 0)
              .map((r) => (
                <li key={r.id} className="flex flex-col gap-1 py-2">
                  <div className="flex justify-between gap-3">
                    <span>
                      {MESES[r.billing_month - 1]} {r.billing_year}
                    </span>
                    <span
                      className={
                        r.diff_cents! < 0 ? 'text-[var(--peligro)]' : 'text-[var(--exito)]'
                      }
                    >
                      {formatCentsExact(r.diff_cents)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-[var(--tinta-suave)]">
                    <span>Sistema anterior</span>
                    <span>{formatCentsExact(r.legacy_cents)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-[var(--tinta-suave)]">
                    <span>Este sistema</span>
                    <span>{formatCentsExact(r.computed_cents)}</span>
                  </div>
                  <span
                    className={`text-xs ${
                      r.kind === 'error_migracion'
                        ? 'font-semibold text-[var(--peligro)]'
                        : 'text-[var(--aviso)]'
                    }`}
                  >
                    {ETIQUETA_CLASE[r.kind] ?? r.kind}
                    {r.reason ? ` — ${r.reason}` : ''}
                  </span>
                </li>
              ))}
          </ul>
        )}
      </div>
    </section>
  );
}
